import {
  DeploymentRingFilter,
  DeploymentRingFilterValues
} from "modules/field-mgmt/sections/device/field-device-deployment-rings/DeploymentRingFilter";
import { DeploymentRingTable } from "modules/field-mgmt/sections/device/field-device-deployment-rings/DeploymentRingTable";
import React from "react";

import {
  FieldItem,
  Heading,
  ISelection,
  Selection,
  SelectionMode,
  Stack,
  useFormContext,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { DeploymentRing } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useDeploymentRings } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { BulkAssignDeploymentRingFormValues } from "../bulk-actions/formTypes";

interface SelectDeploymentRingFieldItemProps {
  fieldName: string;
}

export const SelectDeploymentRingFieldItem: React.FC<SelectDeploymentRingFieldItemProps> = ({
  fieldName
}) => {
  const theme = useTheme();
  const query = useDeploymentRings();

  const { state, actions } = useFormContext<
    BulkAssignDeploymentRingFormValues
  >();

  // Required to register deploymentRingId field since it does not exist af field control
  !state.fields[fieldName] && actions.registerField(fieldName);

  return (
    <Stack verticalFill tokens={{ childrenGap: theme.spacing.s1 }}>
      <Heading variant="modal-heading">Select Deployment Ring</Heading>

      <QueryStateIndicator<DeploymentRing[]> allowNullOrUndefined {...query}>
        {deploymentRings => (
          <Stack verticalFill>
            <Stack
              verticalFill
              verticalAlign="space-between"
              tokens={{ childrenGap: theme.spacing.s1 }}
            >
              <DeploymentRingFilter>
                {({ values }) => {
                  const selection = new Selection<DeploymentRing>({
                    getKey: item => item.id, // This is required to keep the selected item selected when the filter changes (otherwise it uses the index as the key)
                    onSelectionChanged: async () => {
                      const deploymentRings = selection.getSelection();
                      const deploymentRingValue =
                        deploymentRings.length > 0 ? deploymentRings[0].id : "";

                      actions.setValue(fieldName, deploymentRingValue);
                    }
                  });

                  return (
                    <FieldItem
                      name={fieldName}
                      errorMessage={
                        state.triedToSubmit && state.fields[fieldName]?.error
                          ? state.fields[fieldName].error
                          : undefined
                      }
                    >
                      <DeploymentRingTable
                        items={filterAndSortDeploymentRings(
                          deploymentRings,
                          values
                        )}
                        stickyHeader
                        selectionMode={SelectionMode.single}
                        selection={selection as ISelection}
                        setKey={`${fieldName}-Table`}
                      />
                    </FieldItem>
                  );
                }}
              </DeploymentRingFilter>
            </Stack>
          </Stack>
        )}
      </QueryStateIndicator>
    </Stack>
  );
};

const filterAndSortDeploymentRings = (
  deploymentRings: DeploymentRing[],
  filter: DeploymentRingFilterValues
): DeploymentRing[] => {
  return deploymentRings
    .filter(
      d =>
        filter.name === "" ||
        d.displayName.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0
    )
    .sort((a: DeploymentRing, b: DeploymentRing) => {
      if (a.isPublic !== b.isPublic) {
        return a.isPublic ? 1 : -1;
      }

      return 0;
    });
};
